import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { calculateProfileCompleteness } from '../../perfil/components/ProfileCompletenessCard';
import type { DashboardStats, AccountSummary } from '../../../hooks/useDashboardData';

interface Props {
  stats: DashboardStats;
  accounts: AccountSummary[];
  loading: boolean;
}

// ── Score computation ──────────────────────────────────────────────────────────
// Total: 100 pts
// Has accounts (20), Has transactions (20), Has goals (15),
// Profile >=80% (20), Monthly expenses < income (15), More than 3 categories (10)
const computeScore = (
  stats: DashboardStats,
  accountsCount: number,
  profilePct: number
): { score: number; label: string; color: string; breakdown: { label: string; pts: number; earned: boolean }[] } => {
  const breakdown = [
    { label: 'Conta cadastrada', pts: 20, earned: accountsCount > 0 },
    { label: 'Lançamentos registrados', pts: 20, earned: stats.monthlyIncome + stats.monthlyExpenses > 0 },
    { label: 'Meta ativa', pts: 15, earned: stats.activeGoalsCount > 0 },
    { label: 'Perfil completo (≥80%)', pts: 20, earned: profilePct >= 80 },
    { label: 'Gastos menores que receita', pts: 15, earned: stats.monthlyIncome > 0 && stats.monthlyExpenses <= stats.monthlyIncome },
    { label: 'Saldo positivo', pts: 10, earned: stats.totalBalance > 0 },
  ];

  const score = breakdown.reduce((s, b) => s + (b.earned ? b.pts : 0), 0);

  let label = 'Iniciando';
  let color = 'text-gray-500';
  if (score >= 90) { label = 'Excelente'; color = 'text-emerald-600'; }
  else if (score >= 70) { label = 'Muito bom'; color = 'text-emerald-500'; }
  else if (score >= 50) { label = 'Bom'; color = 'text-amber-600'; }
  else if (score >= 30) { label = 'Em progresso'; color = 'text-amber-500'; }

  return { score, label, color, breakdown };
};

const getScoreBarColor = (score: number): string => {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 40) return 'bg-amber-400';
  return 'bg-gray-300';
};

// ── Component ──────────────────────────────────────────────────────────────────
export const UserProgressWidget = ({ stats, accounts, loading }: Props) => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const profilePct = profile ? calculateProfileCompleteness(profile).pct : 0;

  const { score, label, color, breakdown } = useMemo(
    () => computeScore(stats, accounts.length, profilePct),
    [stats, accounts.length, profilePct]
  );

  const nextMilestone = breakdown.find((b) => !b.earned);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-40 mb-4" />
        <div className="h-2 bg-gray-100 rounded w-full mb-3" />
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-50 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-xl">
            <i className="ri-medal-line text-emerald-500 text-sm" />
          </div>
          <h3 className="text-[14px] font-700 text-gray-900">Organização Financeira</h3>
        </div>
        <span className={`text-xs font-700 ${color}`}>{label}</span>
      </div>

      {/* Score bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-400">Score</span>
          <span className={`text-sm font-800 ${color}`}>{score}<span className="text-xs font-500 text-gray-400">/100</span></span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${getScoreBarColor(score)}`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>

      {/* Stats chips */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div
          className="flex flex-col items-center py-2.5 px-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-emerald-50 transition-colors"
          onClick={() => navigate('/contas')}
        >
          <i className="ri-bank-line text-gray-400 text-sm mb-1" />
          <span className="text-sm font-700 text-gray-800">{accounts.length}</span>
          <span className="text-[10px] text-gray-400 text-center leading-tight">conta{accounts.length !== 1 ? 's' : ''}</span>
        </div>
        <div
          className="flex flex-col items-center py-2.5 px-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-emerald-50 transition-colors"
          onClick={() => navigate('/metas')}
        >
          <i className="ri-focus-3-line text-gray-400 text-sm mb-1" />
          <span className="text-sm font-700 text-gray-800">{stats.activeGoalsCount}</span>
          <span className="text-[10px] text-gray-400 text-center leading-tight">meta{stats.activeGoalsCount !== 1 ? 's' : ''} ativa{stats.activeGoalsCount !== 1 ? 's' : ''}</span>
        </div>
        <div
          className={`flex flex-col items-center py-2.5 px-3 rounded-xl cursor-pointer transition-colors ${
            stats.totalBalance >= 0
              ? 'bg-emerald-50 hover:bg-emerald-100'
              : 'bg-red-50 hover:bg-red-100'
          }`}
          onClick={() => navigate('/contas')}
        >
          <i className={`text-sm mb-1 ${stats.totalBalance >= 0 ? 'ri-trending-up-line text-emerald-500' : 'ri-trending-down-line text-red-400'}`} />
          <span className={`text-[10px] font-700 ${stats.totalBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {stats.totalBalance >= 0 ? 'Positivo' : 'Negativo'}
          </span>
          <span className="text-[10px] text-gray-400 text-center leading-tight">saldo</span>
        </div>
      </div>

      {/* Next step suggestion */}
      {nextMilestone && score < 100 && (
        <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
          <i className="ri-lightbulb-line text-amber-500 text-sm mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-amber-700 font-600">Próximo passo (+{nextMilestone.pts} pts)</p>
            <p className="text-[11px] text-amber-600">{nextMilestone.label}</p>
          </div>
        </div>
      )}

      {score >= 100 && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl">
          <i className="ri-trophy-line text-emerald-500 text-sm shrink-0" />
          <p className="text-[11px] text-emerald-700 font-600">Score máximo atingido! Suas finanças estão organizadas.</p>
        </div>
      )}
    </div>
  );
};
